import { Injectable } from '@angular/core';
import { Compilation, TripGenerationLocation, CompilationType } from '../interfaces/compilations.interfaces';
import { Dict } from '../interfaces/common.interfaces';
import { catchError, map, Observable, throwError } from 'rxjs';
import { GeoCoords } from '../interfaces/geo-and-movement.interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EnvProviderService } from './env-provider.service';
import { transformQuestFromServer } from '../functions/quest.functions';
import { transformObeliskFromServer } from '../functions/exploration.functions';
import { transformCompilationFromServer } from '../functions/compilation.functions';

@Injectable({
  providedIn: 'root'
})
export class CompilationsDataService {
  private readonly BASE_URL = `/compilations`;

  constructor(
    private http: HttpClient,
    private env: EnvProviderService
  ) {}

  getCompilations$(
    mode: 'all' | 'nearest' | 'mine' | 'favorites',
    location?: GeoCoords,
    pagination?: { page: number; pageSize: number },
    search?: string
  ): Observable<Compilation[]> {
    let params = new HttpParams();

    if (mode === 'mine') {
      params = params.set('return', 'own');
    } else {
      if (mode === 'favorites') {
        params = params.set('favoriteOnly', 'true');
      }
      params = params.set('return', 'public,own,collaborator,player');
    }

    if (location) {
      params = params.set('location', `${location.lng},${location.lat}`);
    }

    if (pagination) {
      params = params.set('page', pagination.page.toString());
      params = params.set('page_size', pagination.pageSize.toString());
    }

    if (search) {
      params = params.set('search', search);
    }

    return this.http
      .get<Dict<any>[]>(`${this.env.environment.API_URL}${this.BASE_URL}/list`, { params })
      .pipe(map((data: any) => data.map((item) => this.transformFromServer(item))));
  }

  getTrips$(mode: 'all' | 'nearest' | 'mine' | 'favorites'): Observable<Compilation[]> {
    let params = new HttpParams().set('tripsOnly', 'true');

    if (mode === 'mine') {
      params = params.set('return', 'own');
    } else {
      if (mode === 'favorites') {
        params = params.set('favoriteOnly', 'true');
      }
      params = params.set('return', 'public,own,collaborator,player');
    }

    return this.http
      .get<Dict<any>[]>(`${this.env.environment.API_URL}${this.BASE_URL}/list`, { params })
      .pipe(map((data: any) => data.map((item) => this.transformFromServer(item))));
  }

  getCompilation$(id: number): Observable<Compilation> {
    const params = new HttpParams().set('return', 'public,own,collaborator,player');

    return this.http.get<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}`, { params }).pipe(map((data) => this.transformFromServer(data)));
  }

  createCompilation$(
    name: string,
    description: string,
    isPublic: boolean,
    type?: CompilationType,
    activityTags: string[] = [],
    tripConfig?: any,
    quests?: any[],
    obelisks?: any[],
    onlyForEvents?: boolean
  ): Observable<Compilation> {
    const body: any = {
      name,
      description,
      is_public: isPublic
    };

    if (type) {
      body.type = type;
    }

    if (onlyForEvents !== undefined) {
      body.only_for_events = onlyForEvents;
    }

    if (quests && quests.length > 0) {
      body.quests = quests.map((q) => ({ id: q.id }));
    }

    if (obelisks && obelisks.length > 0) {
      body.obelisks = obelisks.map((o) => ({ uuid: o.uuid }));
    }

    if (activityTags?.length > 0) {
      body.activity_tags = activityTags;
    }

    if (tripConfig) {
      body.trip_config = {
        title_image_url: tripConfig.titleImageUrl,
        poi: tripConfig.poi || [],
        paths: tripConfig.paths || [],
        locations: tripConfig.locations || [],
        stats: tripConfig.stats
      };
    }

    return this.http.post<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/create`, body).pipe(map((data) => this.transformFromServer(data)));
  }

  updateCompilation$(id: number, name: string, description: string, isPublic: boolean): Observable<Compilation> {
    const body = {
      name,
      description,
      is_public: isPublic
    };

    return this.http.patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/update`, body).pipe(map((data) => this.transformFromServer(data)));
  }

  updateTripConfig$(id: number, tripData: Partial<Compilation>): Observable<Compilation> {
    const body: any = {
      name: tripData.name,
      description: tripData.description,
      is_public: tripData.isPublic,
      activity_tags: tripData.activityTags || []
    };

    if (tripData.type) {
      body.type = tripData.type;
    }

    if (tripData.onlyForEvents !== undefined) {
      body.only_for_events = tripData.onlyForEvents;
    }

    if (tripData.quests && tripData.quests.length > 0) {
      body.quests = tripData.quests.map((q) => ({ id: q.id }));
    }

    if (tripData.obelisks && tripData.obelisks.length > 0) {
      body.obelisks = tripData.obelisks.map((o) => ({ uuid: o.uuid }));
    }

    if (tripData.tripConfig) {
      body.trip_config = {
        title_image_url: tripData.tripConfig.titleImageUrl,
        poi: tripData.tripConfig.poi || [],
        paths: tripData.tripConfig.paths || [],
        locations: tripData.tripConfig.locations || [],
        stats: tripData.tripConfig.stats
      };
    }

    return this.http.patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/update`, body).pipe(map((data) => this.transformFromServer(data)));
  }

  addQuestToCompilation$(id: number, questId: number): Observable<Compilation> {
    return this.http
      .patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/add-quest/${questId}`, {})
      .pipe(map((data) => this.transformFromServer(data)));
  }

  addObeliskToCompilation$(id: number, obeliskUuid: string): Observable<Compilation> {
    return this.http
      .patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/add-obelisk/${obeliskUuid}`, {})
      .pipe(map((data) => this.transformFromServer(data)));
  }

  deleteCompilation$(id: number): Observable<void> {
    return this.http.delete<void>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/delete`);
  }

  removeQuestFromCompilation$(id: number, questId: number): Observable<Compilation> {
    return this.http
      .patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/remove-quest/${questId}`, {})
      .pipe(map((data) => this.transformFromServer(data)));
  }

  removeObeliskFromCompilation$(id: number, obeliskUuid: string): Observable<Compilation> {
    return this.http
      .patch<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/remove-obelisk/${obeliskUuid}`, {})
      .pipe(map((data) => this.transformFromServer(data)));
  }

  addCompilationToFavorites$(id: number): Observable<void> {
    return this.http.post<void>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/favorite`, {});
  }

  removeCompilationFromFavorites$(id: number): Observable<void> {
    return this.http.delete<void>(`${this.env.environment.API_URL}${this.BASE_URL}/${id}/favorite`);
  }

  createShareToken(compilationId: number): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(`${this.env.environment.API_URL}${this.BASE_URL}/tokens/`, {
      compilation_id: compilationId,
      role: 'player'
    });
  }

  submitShareToken(token: string): Observable<{ compilationId: number }> {
    return this.http
      .patch<any>(`${this.env.environment.API_URL}${this.BASE_URL}/tokens/submit`, { token })
      .pipe(map((value: any) => ({ compilationId: value.compilation_id })));
  }

  // NEW TRIP GENERATION API - 6 Steps

  // Step 1: Request trip generation (unchanged)
  requestTripGeneration$(prompt: string): Observable<{ uuid: string }> {
    return this.http.post<{ uuid: string }>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/request`, { prompt });
  }

  // Step 2: Parse locations
  parseLocations$(uuid: string, coords: string): Observable<any> {
    return this.http.post<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/parse-locations`, { coords });
  }

  // Step 3: Search Overpass for a specific location
  searchOverpass$(uuid: string, locationIndex: number): Observable<any> {
    return this.http.post<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/location/${locationIndex}/search-overpass`, {});
  }

  // Step 4: Search database for a specific location
  searchDatabase$(uuid: string, locationIndex: number): Observable<any> {
    return this.http.post<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/location/${locationIndex}/search-database`, {});
  }

  // Step 5: Deduplicate results for a specific location
  deduplicateResults$(uuid: string, locationIndex: number): Observable<any> {
    return this.http.post<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/location/${locationIndex}/deduplicate`, {});
  }

  // Step 6: Get final results
  getTripResults$(uuid: string): Observable<{ data: TripGenerationLocation[] }> {
    return this.http.get<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/results`).pipe(
      map((response: any) => ({
        data: this.transformTripGenerationResults(response.locations || [])
      }))
    );
  }

  // Regenerate/enhance existing trip
  regenerateTrip$(uuid: string, requestBody: any): Observable<any> {
    return this.http.post<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/regenerate`, requestBody);
  }

  // Enhanced status polling
  tripGenerationStatus$(uuid: string): Observable<any> {
    return this.http.get<any>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/status`);
  }

  // DEPRECATED - Old API methods (kept for compatibility)
  /** @deprecated Use new 6-step API instead */
  startTripGeneration$(uuid: string, location: GeoCoords): Observable<{ data: TripGenerationLocation[]; errorCode?: string; errorMessage?: string }> {
    return this.http
      .post<Dict<any>>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/generate/${uuid}/start`, { coords: `${location.lng},${location.lat}` })
      .pipe(
        catchError((error) => {
          return throwError(() => ({
            errorCode: error.error?.error_code || 'unknown_error',
            errorMessage: error.error?.error_message || 'An unknown error occurred during trip generation.'
          }));
        }),
        map((data: any) => ({
          data: this.transformTripGenerationResults(data.data)
        }))
      );
  }

  saveTripFromGenerated$(uuid: string, body: TripGenerationLocation[], metadata?: Dict<any>): Observable<unknown> {
    return this.http.post<unknown>(`${this.env.environment.API_URL}${this.BASE_URL}/trips/save-from-generated/${uuid}`, {
      locations: body,
      metadata: metadata || {}
    });
  }

  private transformTripGenerationResults(data: any[]): TripGenerationLocation[] {
    return data.map((locationItem: Dict<any>) => ({
      locationName: locationItem['location_name'] || locationItem['name'],
      clusters: locationItem['clusters'].map((cluster: any[]) =>
        cluster.map((clusterItem: Dict<any>) => {
          if (clusterItem['element_type'] === 'obelisk') {
            return transformObeliskFromServer(clusterItem);
          } else if (clusterItem['element_type'] === 'quest') {
            return transformQuestFromServer(clusterItem, this.env.environment.API_URL);
          } else {
            return {
              id: clusterItem['id'],
              name: clusterItem['name'],
              description: clusterItem['description'],
              lng: clusterItem['lng'],
              lat: clusterItem['lat'],
              type: clusterItem['type'],
              tags: clusterItem['tags']
            };
          }
        })
      )
    }));
  }

  private transformFromServer(data: Dict<any>): Compilation {
    return transformCompilationFromServer(data, this.env.environment.API_URL);
  }
}
